import { useEffect, useId, useMemo, useRef } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';

import { cx } from '../design/index.js';
import { BodyScan } from './BodyScan.js';
import { SceneAtmosphere } from './SceneAtmosphere.js';
import { useOrbitTilt } from './useOrbitTilt.js';
import { ariaLabelFor, fractionOf, goalStatus } from './OrbitView.js';
import type { GoalInfo, GoalStatus, OrbitItem } from './OrbitView.js';
import styles from './OrbitStage.module.css';

export interface OrbitStageProps {
  items: OrbitItem[];
  onSelect: (nodeId: string) => void;
  /** Lets the caller place this in a named grid area (see Dashboard.module.css). */
  className?: string;
}

/**
 * The orbit's SVG scene: rings, the travelling light, markers, and — below a
 * density threshold — on-canvas labels. `NodeRail` is the same items in
 * words; this is the same items in space. Split out of the old `OrbitView`
 * so each can sit in its own region of the page (see `Dashboard`'s
 * instrument grid) instead of two columns of one widget.
 */

// ---- Geometry -------------------------------------------------------------
//
// Rings are drawn as plain circles inside a group that is flattened into an
// ellipse with a single CSS transform: `scaleY(var(--orbit-tilt))`, anchored
// at the hub. A rigid rotation would shear an ellipse off its own path (see
// CLAUDE.md's "rotation trap"); a scale does not, and it means the tilt is
// never duplicated as a literal here — every ring reads it from the same
// token tokens.css defines, the same one the rest of the app's orbit uses.
//
// Each marker sits in its own nested group anchored at its mount point on
// that (pre-tilt) circle, counter-scaled by `1 / var(--orbit-tilt)`. Two
// opposite scales around the same point cancel exactly for anything drawn
// relative to that point, so the stem stays a fixed vertical length and the
// marker itself renders as a true circle instead of inheriting the ring's
// squash — while the mount point itself still lands exactly on the ellipse.
//
// The travelling light is a second stroke on the same (pre-tilt) circle,
// dashed and animated by `stroke-dashoffset`. Because the path is a real
// circle, its circumference (2*PI*r) is exact — no elliptic-integral
// approximation (e.g. Ramanujan's) is needed the way it would be for a path
// drawn directly as an SVG <ellipse>, and the loop can never snap.
//
// ---- Depth ---------------------------------------------------------------
//
// The design draws each orbit as two arcs, not one ring: the far half dim
// and painted first, then the figure, then the near half bright on top. That
// is the whole reason the scene reads as a plane with someone standing in it
// rather than a flat target with a sticker on it.
//
// A single <circle> cannot be half behind and half in front of anything, so
// every ring is drawn TWICE — once clipped to the half of the plane above
// the hub (far), once to the half below it (near) — with the figure rendered
// between the two passes. The clip rectangles are declared in the same
// pre-tilt coordinates the circles are, so the flattening transform carries
// them along and the split always lands exactly on the ellipse's vertices.
//
// The travelling light is duplicated the same way, and the two copies are
// created in one commit with identical timing, so they stay in lockstep: one
// bright segment travels the whole ring, dimming as it passes behind.
//
// The figure itself must NOT sit inside the flattened group. The tilt belongs
// to each of its horizontal cross-sections (BodyScan gives every slice an
// `ry` of `rx * tilt`), not to the figure as a whole — putting it in the
// group would squash a standing person to a third of their height.

// The scene is drawn on the design canvas's own scale (design/Main.dc.html
// is 1440x900 with rings of rx 170/258/346), not on an arbitrary smaller
// one. Stroke widths are absolute in user units, so a scene laid out in a
// box half the design's size renders every hairline twice as heavy relative
// to the composition — the rings stop being filaments and the body scan
// stops reading as a scan. Keeping the radii the design's own keeps
// `stroke-width: 1` meaning what it means in the design.
const VIEW_W = 1000;
const VIEW_H = 560;
const CENTER_X = VIEW_W / 2;
const CENTER_Y = 320;

const MIN_RADIUS = 170;
const MAX_RADIUS = 346;
const STEM_LENGTH = 22;
const GOAL_MARKER_R = 10;
const WATCHED_MARKER_R = 9;
const HUB_RADIUS = 6;

/** Padding on the clip rectangles, so a ring's stroke is never shaved along
 *  the outer edge — only along the hub line, which is the split we want. */
const CLIP_PAD = 24;

/** The figure's height, as the design proportions it against the outermost
 *  orbit: 260 units tall against an outer ring of rx 346. Holding that ratio
 *  keeps the head clear of the back arc and the feet crossing the front one
 *  at whatever size this scene is drawn. */
const FIGURE_HEIGHT = MAX_RADIUS * (260 / 346);

/**
 * How far each layer slides with the pointer, in viewBox units. Small numbers
 * on purpose: this is a parallax hint, not a camera.
 *
 * There are exactly two layers, and that is a correctness constraint rather
 * than a simplification. The rings, the figure and the markers are one rigid
 * thing — a plane with someone standing in the middle of it — so they have to
 * travel together. Giving the far and near halves of a ring their own depths
 * pulls one ring into two arcs that no longer meet at the vertices, and drags
 * the travelling light apart at the same seam. It also made a marker's depth
 * depend on which half of the ring its progress happened to land on, so a goal
 * crossing 50% would jump in depth with nothing behind it in the data.
 *
 * Only the atmosphere is genuinely further away, so only the atmosphere gets
 * its own depth. Real parallax across a tilted plane would change the
 * ellipse's shape, not slide its halves; translating the whole scene as one is
 * the honest version of that in two dimensions.
 */
const PARALLAX = {
  atmosphere: { x: 4, y: 2 },
  scene: { x: 14, y: 6 },
} as const;

/** Below this many markers on a ring, a raw fraction-based angle is nudged
 *  just enough to keep neighbours legible; at or above it, faithful spacing
 *  is abandoned in favour of even spacing (order is kept, exact position is
 *  not) — see the report for why. */
const MIN_GAP_DEG = 18;

/** Above this many items total, the scene drops on-canvas text labels and
 *  leaves the dots to speak for themselves; every title is still in
 *  `NodeRail`, which is the point of always rendering both. */
const LABEL_THRESHOLD = 10;

/**
 * With nothing in orbit there is no data to lay a ring's radius out from —
 * `radiusAt` above only exists once there is at least one band. These three
 * are fixed, evenly spaced across the same [MIN_RADIUS, MAX_RADIUS] span a
 * populated scene would use, so the empty plane reads at the same scale a
 * populated one would. They are never `travelling` and always `dim`: the
 * same quiet treatment the watched ring already gets, because a ring with no
 * item on it has nothing to travel toward and claiming otherwise would be a
 * lie about data that doesn't exist. Three is enough to read as a structured
 * plane rather than a single stray circle, and few enough to stay quiet. */
const EMPTY_RING_RADII = [
  MIN_RADIUS,
  MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) / 2,
  MAX_RADIUS,
];

interface PlacedMarker {
  item: OrbitItem;
  kind: 'goal' | 'watched';
  status: GoalStatus | null;
  radius: number;
  angleDeg: number;
}

interface Ring {
  key: string;
  radius: number;
  /** Watched rings never carry the travelling light — see the design note. */
  travelling: boolean;
  dim: boolean;
  speed: number;
}

function normalizeDeg(deg: number): number {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

/** f=0 sits at the left vertex; f increases clockwise; a full lap (f=1) is
 *  the same point again, which is exactly what "achieved" should look like. */
function angleForFraction(fraction: number): number {
  return 180 + fraction * 360;
}

/**
 * Keeps items on a shared ring from overlapping without inventing progress
 * they don't have: order (by raw angle) is always preserved. Up to a point,
 * a later item is nudged forward just enough to keep a minimum gap. Past
 * that point — more items than the ring can legibly hold even nudged — the
 * whole ring falls back to perfectly even spacing, same as the watched ring,
 * because a crowded faithful position is no more readable than no position
 * at all, and `NodeRail` always has the real numbers regardless.
 */
function spreadAngles(rawAngles: number[], minGapDeg: number): number[] {
  const n = rawAngles.length;
  if (n === 0) return [];
  const order = rawAngles
    .map((angle, index) => ({ angle: normalizeDeg(angle), index }))
    .sort((a, b) => a.angle - b.angle);

  const spread = order.map((entry) => entry.angle);
  for (let i = 1; i < n; i++) {
    const min = spread[i - 1]! + minGapDeg;
    if (spread[i]! < min) spread[i] = min;
  }

  if (n > 1 && spread[n - 1]! - spread[0]! > 360 - minGapDeg) {
    const first = spread[0]!;
    for (let i = 0; i < n; i++) spread[i] = first + i * (360 / n);
  }

  const result = new Array<number>(n);
  order.forEach((entry, i) => {
    result[entry.index] = spread[i]!;
  });
  return result;
}

/**
 * Ring and marker placement. Each dated goal gets its own ring, ordered by
 * days remaining so the nearest deadline is innermost; open-ended goals
 * share one ring further out; watched nodes share the outermost ring of
 * all. See `NodeRail`'s legend copy in `Dashboard.tsx` for the same facts in
 * words.
 */
function useLayout(items: OrbitItem[]) {
  return useMemo(() => {
    if (items.length === 0) {
      const rings: Ring[] = EMPTY_RING_RADII.map((radius, i) => ({
        key: `empty-${i}`,
        radius,
        travelling: false,
        dim: true,
        speed: 0,
      }));
      const markers: PlacedMarker[] = [];
      return { rings, markers, showLabels: false };
    }

    const dated = items
      .filter((item): item is OrbitItem & { goal: GoalInfo } => item.goal !== null && item.goal.targetDate !== null)
      .slice()
      .sort((a, b) => (a.goal.progress.daysRemaining ?? Infinity) - (b.goal.progress.daysRemaining ?? Infinity));
    const openEnded = items.filter(
      (item): item is OrbitItem & { goal: GoalInfo } => item.goal !== null && item.goal.targetDate === null,
    );
    const watched = items.filter((item) => item.goal === null);

    const bandCount = dated.length + (openEnded.length > 0 ? 1 : 0) + (watched.length > 0 ? 1 : 0);
    const step = bandCount > 1 ? (MAX_RADIUS - MIN_RADIUS) / (bandCount - 1) : 0;
    const radiusAt = (i: number) => MIN_RADIUS + i * step;

    const rings: Ring[] = [];
    const markers: PlacedMarker[] = [];
    let bandIndex = 0;

    dated.forEach((item) => {
      const radius = radiusAt(bandIndex);
      rings.push({ key: `dated-${item.nodeId}`, radius, travelling: true, dim: false, speed: 16 + bandIndex * 5 });
      markers.push({
        item,
        kind: 'goal',
        status: goalStatus(item.goal),
        radius,
        angleDeg: angleForFraction(fractionOf(item.goal)),
      });
      bandIndex += 1;
    });

    if (openEnded.length > 0) {
      const radius = radiusAt(bandIndex);
      rings.push({ key: 'open-ended', radius, travelling: true, dim: false, speed: 16 + bandIndex * 5 });
      const angles = spreadAngles(
        openEnded.map((item) => angleForFraction(fractionOf(item.goal))),
        MIN_GAP_DEG,
      );
      openEnded.forEach((item, i) => {
        markers.push({ item, kind: 'goal', status: goalStatus(item.goal), radius, angleDeg: angles[i]! });
      });
      bandIndex += 1;
    }

    if (watched.length > 0) {
      const radius = radiusAt(bandIndex);
      rings.push({ key: 'watched', radius, travelling: false, dim: true, speed: 0 });
      watched.forEach((item, i) => {
        markers.push({
          item,
          kind: 'watched',
          status: null,
          radius,
          angleDeg: 180 + i * (360 / watched.length),
        });
      });
      bandIndex += 1;
    }

    return { rings, markers, showLabels: items.length <= LABEL_THRESHOLD };
  }, [items]);
}

function statusDotClass(status: GoalStatus): string {
  if (status === 'achieved') return styles.dotAchieved!;
  if (status === 'overdue') return styles.dotOverdue!;
  return styles.dotOnTrack!;
}

function statusHaloClass(status: GoalStatus): string {
  if (status === 'achieved') return styles.haloAchieved!;
  if (status === 'overdue') return styles.haloOverdue!;
  return styles.haloOnTrack!;
}

/** Where a marker sits in depth. In the pre-tilt circle the hub line is
 *  y = CENTER_Y, so anything above it (sin < 0) is on the far side of the
 *  plane and is painted before the figure; everything else is in front. */
function isFar(marker: PlacedMarker): boolean {
  return Math.sin((marker.angleDeg * Math.PI) / 180) < 0;
}

/**
 * Slides the scene a few units against the pointer, so the layers separate in
 * depth as you move across them. Written to the DOM as two custom properties
 * rather than through React state: this fires on every pointer move, and
 * re-rendering the whole scene at that rate would be wasteful and janky.
 *
 * Skipped entirely without a fine pointer (a touch screen has nothing to
 * follow) or when reduced motion is asked for, in which case no listener is
 * ever attached and the layers stay exactly where they are.
 */
function usePointerParallax<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Where the pointer is asking the scene to go, and where it actually is.
    // The gap between them is the damping: the scene eases after the pointer
    // instead of snapping to it. This is done here rather than with a CSS
    // transition because a transition over a custom-property-driven transform
    // does not reliably fire — see the note on `.parallax`.
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let frame = 0;

    const EASING = 0.12;
    const SETTLED = 0.0005;

    const step = () => {
      currentX += (targetX - currentX) * EASING;
      currentY += (targetY - currentY) * EASING;
      element.style.setProperty('--parallax-x', currentX.toFixed(4));
      element.style.setProperty('--parallax-y', currentY.toFixed(4));

      if (Math.abs(targetX - currentX) > SETTLED || Math.abs(targetY - currentY) > SETTLED) {
        frame = window.requestAnimationFrame(step);
      } else {
        frame = 0;
      }
    };

    const onMove = (event: PointerEvent) => {
      const box = element.getBoundingClientRect();
      if (box.width === 0 || box.height === 0) return;
      // -1..1 across the scene, clamped so a pointer at the far side of a
      // wide page does not fling the layers off their own plane.
      targetX = Math.max(-1, Math.min(1, ((event.clientX - box.left) / box.width - 0.5) * 2));
      targetY = Math.max(-1, Math.min(1, ((event.clientY - box.top) / box.height - 0.5) * 2));
      if (frame === 0) frame = window.requestAnimationFrame(step);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);

  return ref;
}

function parallaxStyle(depth: { x: number; y: number }): CSSProperties {
  return { '--parallax-depth-x': `${depth.x}`, '--parallax-depth-y': `${depth.y}` } as CSSProperties;
}

/**
 * One depth pass over every ring. Rendered twice by the stage — clipped to
 * the far half, then to the near half — so the figure can be painted between
 * the two. Both passes emit identical geometry and identical animation, which
 * is what keeps the travelling light continuous across the seam.
 */
function RingLayer({ rings, clipPathId, far }: { rings: Ring[]; clipPathId: string; far: boolean }) {
  return (
    <g aria-hidden="true" clipPath={`url(#${clipPathId})`}>
      {rings.map((ring) => (
        <circle
          key={ring.key}
          cx={CENTER_X}
          cy={CENTER_Y}
          r={ring.radius}
          className={cx(styles.ringBase, ring.dim && styles.ringBaseWatched, far && styles.ringFar)}
        />
      ))}
      {rings
        .filter((ring) => ring.travelling)
        .map((ring) => (
          <circle
            key={`${ring.key}-travel`}
            cx={CENTER_X}
            cy={CENTER_Y}
            r={ring.radius}
            className={cx(styles.ringTravel, far && styles.ringTravelFar)}
            style={
              {
                '--ring-c': 2 * Math.PI * ring.radius,
                '--ring-speed': ring.speed,
              } as CSSProperties
            }
          />
        ))}
    </g>
  );
}

function Marker({
  marker,
  onSelect,
  softenId,
  index,
}: {
  marker: PlacedMarker;
  onSelect: (nodeId: string) => void;
  softenId: string;
  index: number;
}) {
  const rad = (marker.angleDeg * Math.PI) / 180;
  const mx = CENTER_X + marker.radius * Math.cos(rad);
  const my = CENTER_Y + marker.radius * Math.sin(rad);
  const markerRadius = marker.kind === 'goal' ? GOAL_MARKER_R : WATCHED_MARKER_R;
  const dotY = my - STEM_LENGTH;

  const activate = () => onSelect(marker.item.nodeId);
  const onKeyDown = (event: KeyboardEvent<SVGGElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate();
    }
  };

  return (
    <g className={styles.markerAnchor} style={{ transformOrigin: `${mx}px ${my}px` }}>
      <g
        role="button"
        tabIndex={0}
        aria-label={ariaLabelFor(marker.item)}
        className={styles.marker}
        onClick={activate}
        onKeyDown={onKeyDown}
      >
        <line
          x1={mx}
          y1={my}
          x2={mx}
          y2={dotY}
          className={marker.kind === 'goal' ? styles.stem : styles.stemWatched}
          aria-hidden="true"
        />
        {/* The glow that makes a mark read as emitted rather than painted.
            It breathes, each marker offset from the next, and swells when
            the marker is hovered or focused. */}
        <circle
          cx={mx}
          cy={dotY}
          r={markerRadius * 2.4}
          filter={`url(#${softenId})`}
          className={cx(
            styles.halo,
            marker.kind === 'watched' ? styles.haloWatched : statusHaloClass(marker.status!),
          )}
          style={{ animationDelay: `${(index % 3) * -1.5}s` }}
          aria-hidden="true"
        />
        <circle
          cx={mx}
          cy={dotY}
          r={markerRadius}
          className={cx(
            styles.dot,
            marker.kind === 'watched' ? styles.dotWatched : statusDotClass(marker.status!),
          )}
        />
      </g>
    </g>
  );
}

function MarkerLabel({ marker }: { marker: PlacedMarker }) {
  const rad = (marker.angleDeg * Math.PI) / 180;
  const mx = CENTER_X + marker.radius * Math.cos(rad);
  const my = CENTER_Y + marker.radius * Math.sin(rad);
  return (
    <g className={styles.markerAnchor} style={{ transformOrigin: `${mx}px ${my}px` }}>
      <text
        x={mx}
        // Clear of the halo, which extends 2.4x the dot's own radius.
        y={my - STEM_LENGTH - GOAL_MARKER_R * 2.4 - 8}
        textAnchor="middle"
        className={styles.label}
        aria-hidden="true"
      >
        {marker.item.title}
      </text>
    </g>
  );
}

export function OrbitStage({ items, onSelect, className }: OrbitStageProps) {
  const { rings, markers, showLabels } = useLayout(items);
  const sceneRef = usePointerParallax<HTMLDivElement>();

  // One resolved value for every layer, so the rings, the atmosphere and the
  // figure are all drawn at an identical tilt even on the first frame.
  const tilt = useOrbitTilt();

  // React's generated ids contain colons, which are legal in an id but make
  // for a hostile `url(#...)` reference — strip them.
  const rawId = useId();
  const clipBase = rawId.replace(/:/g, '');
  const farClipId = `${clipBase}-far`;
  const nearClipId = `${clipBase}-near`;
  const softenId = `${clipBase}-soften`;

  // Depth order: markers on the back half of the plane are painted before the
  // figure, the rest after it — the same split the rings get. Tab order
  // follows paint order as a result, which is why NodeRail (always rendered,
  // always in ring order) is the stable way to read the same items.
  const { farMarkers, nearMarkers } = useMemo(
    () => ({
      farMarkers: markers.filter(isFar),
      nearMarkers: markers.filter((marker) => !isFar(marker)),
    }),
    [markers],
  );

  const clipX = CENTER_X - MAX_RADIUS - CLIP_PAD;
  const clipW = 2 * (MAX_RADIUS + CLIP_PAD);
  const clipH = MAX_RADIUS + CLIP_PAD;

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.scene} ref={sceneRef}>
        <svg className={styles.svg} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="xMidYMid meet">
          <defs>
            {/* Declared in pre-tilt coordinates, the same space the circles
                are drawn in, so the flattening transform carries the split
                onto the ellipse's own vertices. */}
            <clipPath id={farClipId}>
              <rect x={clipX} y={CENTER_Y - MAX_RADIUS - CLIP_PAD} width={clipW} height={clipH} />
            </clipPath>
            <clipPath id={nearClipId}>
              <rect x={clipX} y={CENTER_Y} width={clipW} height={clipH} />
            </clipPath>
            <filter id={softenId} x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="7" />
            </filter>
          </defs>

          <g className={styles.drift}>
            {/* ---- Behind the figure ---------------------------------- */}
            <g className={styles.parallax} style={parallaxStyle(PARALLAX.atmosphere)}>
              <g className={cx(styles.layer, styles.layerAtmosphere)}>
                <SceneAtmosphere
                  viewBoxWidth={VIEW_W}
                  viewBoxHeight={VIEW_H}
                  centerX={CENTER_X}
                  centerY={CENTER_Y}
                  spokeInnerRadius={MIN_RADIUS * 0.45}
                  spokeOuterRadius={MAX_RADIUS * 1.15}
                  glowRadiusX={MAX_RADIUS * 1.05}
                  pingRadiusX={MIN_RADIUS * 0.8}
                  // Grain is painted full-bleed by the page's projection
                  // overlay instead: confined to this SVG it would draw a
                  // visible rectangle wherever the scene's box ends.
                  grainOpacity={0}
                  tilt={tilt}
                />
              </g>
            </g>

            {/* One parallax group for the entire scene — see PARALLAX. */}
            <g className={styles.parallax} style={parallaxStyle(PARALLAX.scene)}>
              <g className={cx(styles.layer, styles.layerRings)}>
                <g className={styles.plane} style={{ transformOrigin: `${CENTER_X}px ${CENTER_Y}px` }}>
                  <RingLayer rings={rings} clipPathId={farClipId} far />
                  {farMarkers.map((marker, index) => (
                    <Marker
                      key={marker.item.nodeId}
                      marker={marker}
                      onSelect={onSelect}
                      softenId={softenId}
                      index={index}
                    />
                  ))}
                  {showLabels &&
                    farMarkers.map((marker) => (
                      <MarkerLabel key={`${marker.item.nodeId}-label`} marker={marker} />
                    ))}
                </g>
              </g>

              {/* ---- The person these aspects belong to ----------------
                  Outside the flattened group on purpose: the tilt belongs to
                  each of its cross-sections, not to the figure as a whole. */}
              <g className={cx(styles.layer, styles.layerFigure)}>
                <BodyScan cx={CENTER_X} cy={CENTER_Y} height={FIGURE_HEIGHT} tilt={tilt} />
              </g>

              {/* ---- In front of the figure ---------------------------- */}
              <g className={cx(styles.layer, styles.layerNear)}>
                <g className={styles.plane} style={{ transformOrigin: `${CENTER_X}px ${CENTER_Y}px` }}>
                  <RingLayer rings={rings} clipPathId={nearClipId} far={false} />
                  <circle cx={CENTER_X} cy={CENTER_Y} r={HUB_RADIUS} className={styles.hub} aria-hidden="true" />
                  {nearMarkers.map((marker, index) => (
                    <Marker
                      key={marker.item.nodeId}
                      marker={marker}
                      onSelect={onSelect}
                      softenId={softenId}
                      index={index + farMarkers.length}
                    />
                  ))}
                  {showLabels &&
                    nearMarkers.map((marker) => (
                      <MarkerLabel key={`${marker.item.nodeId}-label`} marker={marker} />
                    ))}
                </g>
              </g>
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
}
